import React from 'react';
import Layout from '../../Layout';

const getEmbedSrc = ({
  universityId,
  apiKey,
  theme = 'default',
  showFilters = true,
  itemsPerPage = 10,
} = {}) => {
  // Validate required parameters
  if (!universityId || !apiKey) {
    throw new Error('universityId and apiKey are required for embedding PhD opportunities');
  }

  // Validate universityId format (should be a UUID)
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  if (!uuidRegex.test(universityId)) {
    throw new Error('universityId must be a valid UUID format');
  }

  // Validate apiKey format (should be a 64-character hex string)
  const apiKeyRegex = /^[0-9a-f]{64}$/i;
  if (!apiKeyRegex.test(apiKey)) {
    throw new Error('apiKey must be a valid 64-character hexadecimal string');
  }

  // Build query parameters with proper encoding
  const params = new URLSearchParams({
    university_id: universityId,
    api_key: apiKey,
    theme: theme,
    show_filters: showFilters.toString(),
    items_per_page: itemsPerPage.toString(),
  });

  // Determine base URL based on environment
  const hostname = window.location.hostname;
  let baseUrl;
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    baseUrl = 'http://localhost:8181';
  } else {
    baseUrl = 'https://phdscanner.com';
  }
  
  return `${baseUrl}/embed/opportunities?${params.toString()}`;
};

const EmbedTestPage = () => {
  // Hardcoded values for the embed
  const universityId = '11111111-1111-1111-1111-111111111111';
  const apiKey = '304cba5e79e563b5b070914d217541a22f1d76937cd123df46aa3de32129323d';
  const theme = 'blue';
  const showFilters = true;
  const itemsPerPage = 10;

  // Hide loading overlay after 10 seconds as fallback
  React.useEffect(() => {
    const timer = setTimeout(() => {
      const loadingElement = document.getElementById('embed-loading');
      if (loadingElement) {
        loadingElement.style.display = 'none';
      }
    }, 10000);

    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* University-style header */}
      <div className="bg-gradient-to-r from-blue-800 to-blue-600 text-white py-8">
        <div className="max-w-6xl mx-auto px-4">
          <h1 className="text-4xl font-bold mb-2">Oxford University</h1>
          <p className="text-xl opacity-90">Research Opportunities & PhD Programs</p>
        </div>
      </div>

      {/* University navigation */}
      <nav className="bg-white shadow-sm border-b">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex space-x-8 py-4">
            <a href="#" className="text-gray-700 hover:text-blue-600">Home</a>
            <a href="#" className="text-gray-700 hover:text-blue-600">About</a>
            <a href="#" className="text-gray-700 hover:text-blue-600">Research</a>
            <a href="#" className="text-blue-600 font-medium">PhD Opportunities</a>
            <a href="#" className="text-gray-700 hover:text-blue-600">Contact</a>
          </div>
        </div>
      </nav>

      {/* Main content area */}
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* University content before embed */}
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">PhD Opportunities at Oxford University</h2>
          <p className="text-lg text-gray-600 mb-6">
            Discover exciting research opportunities and PhD programs available at our university. 
            Browse through our current openings and find the perfect research position for your academic career.
          </p>
        </div>

        {/* START: University Integration Code */}
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="p-6 border-b bg-gray-50">
            <h3 className="text-xl font-semibold text-gray-900">Explore Our Latest PhD Opportunities</h3>
            <p className="text-gray-600 mt-1">
              Powered by <a href="https://phdscanner.com" target="_blank" rel="noopener noreferrer" className="font-semibold text-blue-600 hover:underline">PhDScanner.com</a>
            </p>
          </div>
          <div className="relative">
            <iframe 
              src="https://phdscanner.com/embed/opportunities?university_id=11111111-1111-1111-1111-111111111111&api_key=824cd53532b0e9bdd1be395dce7622dd671d2a713e1ce6621334df94bb719237&theme=default&show_filters=true&items_per_page=10"
              width="100%" 
              height="800"
              title="PhD Opportunities"
              loading="lazy"
              className="border-0"
              sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
            ></iframe>
          </div>
        </div>
        {/* END: University Integration Code */}

        {/* University content after embed */}
        <div className="mt-8 bg-white rounded-lg shadow p-6">
          <h3 className="text-2xl font-bold text-gray-900 mb-4">Why Choose Oxford University?</h3>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h4 className="text-lg font-semibold text-gray-900 mb-2">World-Class Research</h4>
              <p className="text-gray-600">Our university is known for cutting-edge research across multiple disciplines.</p>
            </div>
            <div>
              <h4 className="text-lg font-semibold text-gray-900 mb-2">Expert Supervision</h4>
              <p className="text-gray-600">Work with internationally recognized researchers and academics.</p>
            </div>
            <div>
              <h4 className="text-lg font-semibold text-gray-900 mb-2">Modern Facilities</h4>
              <p className="text-gray-600">Access to state-of-the-art laboratories and research facilities.</p>
            </div>
            <div>
              <h4 className="text-lg font-semibold text-gray-900 mb-2">Career Support</h4>
              <p className="text-gray-600">Comprehensive support for your academic and professional development.</p>
            </div>
          </div>
        </div>

        {/* Integration info for developers */}
        <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-blue-900 mb-3">🔧 Integration Information</h3>
          <p className="text-blue-800 mb-4">
            This page demonstrates how universities can integrate PhDScanner into their websites. 
            The iframe above shows real PhD opportunities from your test customer.
          </p>
          <div className="bg-gray-900 text-green-400 p-4 rounded text-sm font-mono overflow-x-auto">
            <div>// Integration code for universities:</div>
            <div>&lt;iframe </div>
            <div>&nbsp;&nbsp;src="https://phdscanner.com/embed/opportunities?university_id=YOUR_UUID&api_key=YOUR_64_CHAR_HEX_KEY"</div>
            <div>&nbsp;&nbsp;width="100%" height="800"</div>
            <div>&nbsp;&nbsp;title="PhD Opportunities"</div>
            <div>&nbsp;&nbsp;loading="lazy"</div>
            <div>&nbsp;&nbsp;sandbox="allow-scripts allow-same-origin allow-forms allow-popups"</div>
            <div>&gt;&lt;/iframe&gt;</div>
            <div className="mt-4 text-yellow-400">// Required parameters:</div>
            <div className="text-yellow-400">// university_id: Valid UUID format (e.g., 123e4567-e89b-12d3-a456-426614174000)</div>
            <div className="text-yellow-400">// api_key: 64-character hexadecimal string</div>
          </div>
        </div>
      </div>

      {/* University footer */}
      <footer className="bg-gray-800 text-white py-8 mt-12">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-8">
            <div>
              <h4 className="text-lg font-semibold mb-4">Oxford University</h4>
              <p className="text-gray-300">Excellence in research and education since 1900.</p>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2 text-gray-300">
                <li><a href="#" className="hover:text-white">Admissions</a></li>
                <li><a href="#" className="hover:text-white">Research</a></li>
                <li><a href="#" className="hover:text-white">Library</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">Contact</h4>
              <p className="text-gray-300">Email: info@universityoftest.edu</p>
              <p className="text-gray-300">Phone: +1 234 567 8900</p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default EmbedTestPage; 